import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Stack, Tooltip, Typography } from '@mui/material';
import { EnrollmentScoreDetailResponse, ScoreComponentDto } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MprcContainer, MprcRequestErrorReport, MprcSpinnerIndicator } from '../../../../../components';
import { MprcSimpleDialog } from '../../../../../components/MprcSimpleDialog';
import { ScoreResult } from './ScoreResult';

/////////////////////////////////////////////////////////////////////////////////////

interface EnrollmentScoresProps {
    data: ScoreComponentDto[];
}

export const EnrollmentScores: React.FC<EnrollmentScoresProps> = ({ data }) => {

    const { t } = useTranslation("lms");
    return (
        <>
            <Stack gap={0.5}>
                <Grid container size={{ xs: 12 }} spacing={2}>
                    <Grid size="grow">
                        <Typography variant="overline">{t("Student.EnrollmentView.ScoreDetailDialog.ScoreItemsText")}</Typography>
                    </Grid>
                    <Grid sx={{ width: 100, textAlign: "right" }}>
                        <Typography variant="overline">{t("Student.EnrollmentView.ScoreDetailDialog.WeightText")}</Typography>
                    </Grid>
                    <Grid sx={{ width: 100, textAlign: "right" }}>
                        <Typography variant="overline">{t("Student.EnrollmentView.ScoreDetailDialog.ScoreText")}</Typography>
                    </Grid>
                </Grid>
            </Stack>
            {data.map(item =>
                <Grid key={item.componentId} container size={{ xs: 12 }} spacing={2}>
                    <Grid size="grow">
                        {item.name}
                    </Grid>
                    <Grid sx={{ width: 100, textAlign: "right" }}>
                        {item.weight}
                    </Grid>
                    <Grid sx={{ width: 100, textAlign: "right" }} >
                        {item.pendingScore
                            ? <Tooltip title={t("Student.EnrollmentView.ScoreDetailDialog.PendingScoreTooltip")}><FontAwesomeIcon icon={faClock} /></Tooltip>
                            : item.score != null ? `${item.score}%` : "--"}
                    </Grid>
                </Grid>
            )}
        </>
    );
};


/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    enrollmentId: string;
    onClose: () => void;
}

export const ScoreDetailsDialog: React.FC<IProps> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const { enrollmentId, onClose } = props;

    const [loading, setLoading] = useState<boolean>(true);
    const [viewData, setViewData] = useState<EnrollmentScoreDetailResponse>();
    const [viewError, setViewError] = useState<any>();

    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        api.v1.enrollment.getScoreDetail({ enrollmentId })
            .then(response => setViewData(response.data))
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <MprcSimpleDialog
                width="sm"
                title={t("Student.EnrollmentView.ScoreDetailDialog.Title")}
                subtitle={viewData?.trainingName ?? ""}
                message={
                    <>
                        <MprcRequestErrorReport error={viewError} />
                        {loading
                            ? <MprcSpinnerIndicator />
                            : viewData &&
                            <>
                                <MprcContainer>
                                    <ScoreResult
                                        variant="multiple"
                                        enrollmentId={enrollmentId}
                                        score={viewData.scorePercent}
                                        minScore={viewData.minFinalScore}
                                        isClosed={viewData.isFinalized}
                                        fullWidth
                                        totalItems={viewData.totalItems}
                                        totalCompletedItems={viewData.totalCompletedItems}
                                        status={viewData.status}
                                    />
                                </MprcContainer>
                                <EnrollmentScores data={viewData.scoreComponents} />
                            </>
                        }
                    </>
                }
                onClose={onClose}
            />
        </>
    );
};
